import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Calendar,
  Plus,
  Loader2,
  AlertCircle,
  Edit2,
  Trash2,
  MapPin,
  Clock,
  Users,
  Repeat,
  Search,
  UserCheck,
  List,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'

interface Event {
  id: string
  church_id: string
  title: string
  description: string | null
  event_type: string | null
  start_datetime: string
  end_datetime: string | null
  location: string | null
  recurring_pattern: any
  max_attendees: number | null
  registration_required: boolean
  registration_deadline: string | null
  is_active: boolean
  created_at: string
  created_by: string | null
  // Stats from event_stats view
  total_registrations?: number
  confirmed_registrations?: number
  waitlist_count?: number
  checked_in_count?: number
  spots_remaining?: number
  is_full?: boolean
}

interface EventRegistration {
  id: string
  event_id: string
  member_id: string | null
  user_id: string | null
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  registration_status: string
  registered_at: string
  checked_in_at: string | null
  checked_out_at: string | null
  attendance_status: string | null
  notes: string | null
}

interface EventsProps {
  user?: AdminUser | null
}

const EVENT_TYPES = [
  { value: 'service', label: 'Service', color: 'bg-blue-500' },
  { value: 'prayer_meeting', label: 'Prayer Meeting', color: 'bg-sky-500' },
  { value: 'bible_study', label: 'Bible Study', color: 'bg-green-500' },
  { value: 'conference', label: 'Conference', color: 'bg-orange-500' },
  { value: 'workshop', label: 'Workshop', color: 'bg-yellow-500' },
  { value: 'outreach', label: 'Outreach', color: 'bg-cyan-400' },
  { value: 'youth_meeting', label: 'Youth Meeting', color: 'bg-indigo-500' },
  { value: 'other', label: 'Other', color: 'bg-gray-500' },
]

export default function Events({ user }: EventsProps) {
  const [loading, setLoading] = useState(true)
  const [events, setEvents] = useState<Event[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')

  // Dialog states
  const [showEventDialog, setShowEventDialog] = useState(false)
  const [editingEvent, setEditingEvent] = useState<Event | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Registration management states
  const [showRegistrationsDialog, setShowRegistrationsDialog] = useState(false)
  const [selectedEventForRegistrations, setSelectedEventForRegistrations] = useState<Event | null>(null)
  const [registrations, setRegistrations] = useState<EventRegistration[]>([])
  const [loadingRegistrations, setLoadingRegistrations] = useState(false)
  const [checkingIn, setCheckingIn] = useState<string | null>(null)

  // Form state
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_type: 'service',
    start_datetime: '',
    end_datetime: '',
    location: '',
    max_attendees: '',
    registration_required: false,
    registration_deadline: '',
    is_active: true,
  })

  useEffect(() => {
    loadEvents()
  }, [user])

  async function loadEvents() {
    try {
      setLoading(true)

      if (!user?.churchId) {
        console.error('No church ID found')
        return
      }

      // Use event_stats view to get registration statistics
      const { data, error } = await supabase
        .from('event_stats')
        .select('*')
        .eq('church_id', user.churchId)
        .order('start_datetime', { ascending: false })

      if (error) throw error

      setEvents(data || [])
    } catch (error) {
      console.error('Error loading events:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadEventRegistrations(eventId: string) {
    try {
      setLoadingRegistrations(true)

      const { data, error } = await supabase
        .from('event_registrations')
        .select('*')
        .eq('event_id', eventId)
        .order('registered_at', { ascending: false })

      if (error) throw error

      setRegistrations(data || [])
    } catch (error) {
      console.error('Error loading registrations:', error)
    } finally {
      setLoadingRegistrations(false)
    }
  }

  async function handleCheckIn(registrationId: string) {
    try {
      setCheckingIn(registrationId)

      const { data, error } = await supabase.rpc('check_in_to_event', {
        p_registration_id: registrationId,
      })

      if (error) throw error

      if (data && !data.success) {
        alert(data.error || 'Failed to check in')
        return
      }

      // Reload registrations to show updated status
      if (selectedEventForRegistrations) {
        await loadEventRegistrations(selectedEventForRegistrations.id)
      }
    } catch (error: any) {
      console.error('Error checking in:', error)
      alert('Failed to check in: ' + error.message)
    } finally {
      setCheckingIn(null)
    }
  }

  function openRegistrationsDialog(event: Event) {
    setSelectedEventForRegistrations(event)
    setShowRegistrationsDialog(true)
    loadEventRegistrations(event.id)
  }

  function openCreateDialog() {
    setEditingEvent(null)
    setEventForm({
      title: '',
      description: '',
      event_type: 'service',
      start_datetime: '',
      end_datetime: '',
      location: '',
      max_attendees: '',
      registration_required: false,
      registration_deadline: '',
      is_active: true,
    })
    setShowEventDialog(true)
  }

  function openEditDialog(event: Event) {
    setEditingEvent(event)
    setEventForm({
      title: event.title,
      description: event.description || '',
      event_type: event.event_type || 'service',
      start_datetime: event.start_datetime ? event.start_datetime.slice(0, 16) : '',
      end_datetime: event.end_datetime ? event.end_datetime.slice(0, 16) : '',
      location: event.location || '',
      max_attendees: event.max_attendees?.toString() || '',
      registration_required: event.registration_required,
      registration_deadline: event.registration_deadline ? event.registration_deadline.slice(0, 16) : '',
      is_active: event.is_active,
    })
    setShowEventDialog(true)
  }

  async function handleSaveEvent() {
    if (!user?.churchId || !user?.id) return

    try {
      setSaving(true)

      const eventData = {
        church_id: user.churchId,
        title: eventForm.title,
        description: eventForm.description || null,
        event_type: eventForm.event_type,
        start_datetime: eventForm.start_datetime,
        end_datetime: eventForm.end_datetime || null,
        location: eventForm.location || null,
        max_attendees: eventForm.max_attendees ? parseInt(eventForm.max_attendees) : null,
        registration_required: eventForm.registration_required,
        registration_deadline: eventForm.registration_deadline || null,
        is_active: eventForm.is_active,
        created_by: user.id,
      }

      if (editingEvent) {
        // Update existing event
        const { error } = await supabase
          .from('events')
          .update(eventData)
          .eq('id', editingEvent.id)

        if (error) throw error
      } else {
        // Create new event
        const { error } = await supabase
          .from('events')
          .insert([eventData])

        if (error) throw error
      }

      await loadEvents()
      setShowEventDialog(false)
    } catch (error: any) {
      console.error('Error saving event:', error)
      alert('Failed to save event: ' + error.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteEvent(eventId: string) {
    if (!confirm('Are you sure you want to delete this event?')) return

    try {
      setDeleting(true)

      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId)

      if (error) throw error

      await loadEvents()
    } catch (error: any) {
      console.error('Error deleting event:', error)
      alert('Failed to delete event: ' + error.message)
    } finally {
      setDeleting(false)
    }
  }

  function getEventTypeConfig(type: string | null) {
    return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[EVENT_TYPES.length - 1]
  }

  function formatDateTime(datetime: string | null) {
    if (!datetime) return '-'
    return new Date(datetime).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'all' || event.event_type === filterType
    return matchesSearch && matchesType
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading events...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Events</h1>
          <p className="text-muted-foreground">Manage church events and activities</p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Create Event
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {EVENT_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Events Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Events ({filteredEvents.length})</CardTitle>
          <CardDescription>View and manage church events</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Date & Time</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Registration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEvents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {searchTerm ? 'No events found matching your search.' : 'No events yet. Create your first event!'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredEvents.map((event) => {
                  const typeConfig = getEventTypeConfig(event.event_type)
                  return (
                    <TableRow key={event.id}>
                      <TableCell>
                        <div className="flex items-start gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground mt-1" />
                          <div>
                            <div className="font-medium">{event.title}</div>
                            {event.description && (
                              <div className="text-sm text-muted-foreground line-clamp-1">
                                {event.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={typeConfig.color + ' text-white'}>
                          {typeConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDateTime(event.start_datetime)}
                          </div>
                          {event.end_datetime && (
                            <div className="text-muted-foreground text-xs">
                              to {formatDateTime(event.end_datetime)}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {event.location ? (
                          <div className="flex items-center gap-1 text-sm">
                            <MapPin className="h-3 w-3" />
                            {event.location}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {event.registration_required ? (
                          <div className="text-sm space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="mb-1">
                                <Users className="h-3 w-3 mr-1" />
                                {event.confirmed_registrations || 0}
                                {event.max_attendees ? ` / ${event.max_attendees}` : ''}
                              </Badge>
                              {event.is_full && (
                                <Badge variant="destructive" className="text-xs">Full</Badge>
                              )}
                            </div>
                            {event.checked_in_count !== undefined && event.checked_in_count > 0 && (
                              <div className="text-xs text-green-600 flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                {event.checked_in_count} checked in
                              </div>
                            )}
                            {event.waitlist_count !== undefined && event.waitlist_count > 0 && (
                              <div className="text-xs text-orange-600">
                                +{event.waitlist_count} waitlist
                              </div>
                            )}
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">
                            Not Required
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={event.is_active ? 'default' : 'secondary'}>
                          {event.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {event.registration_required && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRegistrationsDialog(event)}
                              title="View Registrations"
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <List className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(event)}
                            title="Edit Event"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteEvent(event.id)}
                            disabled={deleting}
                            title="Delete Event"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
            <DialogDescription>
              {editingEvent ? 'Update event details' : 'Add a new event to your church calendar'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Event Title *</Label>
              <Input
                id="title"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="Sunday Service, Prayer Meeting, etc."
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="Event details..."
                rows={3}
              />
            </div>

            {/* Event Type */}
            <div className="space-y-2">
              <Label htmlFor="event_type">Event Type</Label>
              <Select
                value={eventForm.event_type}
                onValueChange={(value) => setEventForm({ ...eventForm, event_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_datetime">Start Date & Time *</Label>
                <Input
                  id="start_datetime"
                  type="datetime-local"
                  value={eventForm.start_datetime}
                  onChange={(e) => setEventForm({ ...eventForm, start_datetime: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_datetime">End Date & Time</Label>
                <Input
                  id="end_datetime"
                  type="datetime-local"
                  value={eventForm.end_datetime}
                  onChange={(e) => setEventForm({ ...eventForm, end_datetime: e.target.value })}
                />
              </div>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                placeholder="Church auditorium, Online, etc."
              />
            </div>

            {/* Registration Settings */}
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="registration_required">Require Registration</Label>
                  <p className="text-sm text-muted-foreground">
                    Members must register to attend this event
                  </p>
                </div>
                <Switch
                  id="registration_required"
                  checked={eventForm.registration_required}
                  onCheckedChange={(checked) =>
                    setEventForm({ ...eventForm, registration_required: checked })
                  }
                />
              </div>

              {eventForm.registration_required && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="max_attendees">Maximum Attendees</Label>
                    <Input
                      id="max_attendees"
                      type="number"
                      value={eventForm.max_attendees}
                      onChange={(e) => setEventForm({ ...eventForm, max_attendees: e.target.value })}
                      placeholder="Leave empty for unlimited"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="registration_deadline">Registration Deadline</Label>
                    <Input
                      id="registration_deadline"
                      type="datetime-local"
                      value={eventForm.registration_deadline}
                      onChange={(e) =>
                        setEventForm({ ...eventForm, registration_deadline: e.target.value })
                      }
                    />
                  </div>
                </>
              )}
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="is_active">Active Status</Label>
                <p className="text-sm text-muted-foreground">
                  Inactive events are hidden from members
                </p>
              </div>
              <Switch
                id="is_active"
                checked={eventForm.is_active}
                onCheckedChange={(checked) => setEventForm({ ...eventForm, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEvent}
              disabled={saving || !eventForm.title || !eventForm.start_datetime}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  {editingEvent ? 'Update Event' : 'Create Event'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Registrations Dialog */}
      <Dialog open={showRegistrationsDialog} onOpenChange={setShowRegistrationsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>Event Registrations - {selectedEventForRegistrations?.title}</DialogTitle>
            <DialogDescription>
              View and manage registrations for this event
            </DialogDescription>
          </DialogHeader>

          {loadingRegistrations ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : registrations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No registrations yet for this event.
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{registrations.length}</div>
                    <p className="text-xs text-muted-foreground">Total Registrations</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">
                      {registrations.filter(r => r.checked_in_at).length}
                    </div>
                    <p className="text-xs text-muted-foreground">Checked In</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-blue-600">
                      {registrations.filter(r => r.registration_status === 'registered').length}
                    </div>
                    <p className="text-xs text-muted-foreground">Confirmed</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-orange-600">
                      {registrations.filter(r => r.registration_status === 'waitlist').length}
                    </div>
                    <p className="text-xs text-muted-foreground">Waitlist</p>
                  </CardContent>
                </Card>
              </div>

              {/* Registrations Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead>Check-in</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {registrations.map((reg) => (
                    <TableRow key={reg.id}>
                      <TableCell>
                        <div className="font-medium">
                          {reg.first_name && reg.last_name
                            ? `${reg.first_name} ${reg.last_name}`
                            : 'Anonymous'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {reg.email && <div>{reg.email}</div>}
                          {reg.phone && (
                            <div className="text-muted-foreground">{reg.phone}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            reg.registration_status === 'registered'
                              ? 'default'
                              : reg.registration_status === 'waitlist'
                              ? 'secondary'
                              : 'outline'
                          }
                        >
                          {reg.registration_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(reg.registered_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {reg.checked_in_at ? (
                          <div className="text-sm">
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="h-4 w-4" />
                              Checked In
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(reg.checked_in_at).toLocaleTimeString()}
                            </div>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">
                            Not Checked In
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {!reg.checked_in_at && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCheckIn(reg.id)}
                            disabled={checkingIn === reg.id}
                            className="text-green-600 hover:text-green-700"
                          >
                            {checkingIn === reg.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <UserCheck className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegistrationsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
